import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';

const DISPLAY_MS = 2800;
const EXIT_MS = 380;

/**
 * Centered boss-name bar for Shadow Negara mimic turns (mission 75).
 * Reuses the same presentation as the EU boss mulligan intro (HUMANITY, etc.).
 *
 * @param {{ visible: boolean, translationKey: string | null }} props
 */
export default function ExploreUnknownNegaraBossMimicIntro({ visible, translationKey }) {
  const { t } = useTranslation();
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const timersRef = useRef([]);

  useEffect(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];

    if (!visible || !translationKey) {
      setMounted(false);
      setExiting(false);
      return undefined;
    }

    setMounted(true);
    setExiting(false);

    const volume = getEffectiveSoundOutputVolume(0.75);
    if (volume !== null) {
      const audio = new Audio('/sounds/ahhh.mp3');
      audio.volume = volume;
      audio.play().catch(() => {});
    }

    timersRef.current.push(
      window.setTimeout(() => setExiting(true), DISPLAY_MS - EXIT_MS),
      window.setTimeout(() => {
        setMounted(false);
        setExiting(false);
      }, DISPLAY_MS)
    );

    return () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };
  }, [visible, translationKey]);

  if (!mounted || !translationKey) {
    return null;
  }

  const root = (
    <div
      className="battle-intro-overlay"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'transparent',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
      }}
    >
      <div
        className={`battle-intro-boss-bar-wrap${exiting ? ' battle-intro-boss-bar-wrap--exit' : ''}`}
        role="img"
        aria-label={t(translationKey)}
      >
        <div className="battle-intro-boss-bar">
          <div className="battle-intro-boss-bar-border battle-intro-boss-bar-border--top" aria-hidden="true" />
          <h1 className="battle-intro-boss-name">{t(translationKey)}</h1>
          <div className="battle-intro-boss-bar-border battle-intro-boss-bar-border--bottom" aria-hidden="true" />
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return root;
  }
  return createPortal(root, document.body);
}
